import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Loader2, Send, CheckCircle, Truck, XCircle, Pencil, Save,
  MessageSquare, FileText, Plus, Ship,
} from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type {
  CallOff, CallOffLine, CallOffTimelineEvent, CallOffEditHistoryEntry,
  CallOffMessage, AvailablePartForCallOff, CallOffDeliveryDestination,
  BatchInlineEditChange,
} from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { toast } from 'sonner';
import CallOffLinesTable from './CallOffLinesTable';
import CallOffDetailTab from './CallOffDetailTab';
import CallOffCommunicationTab from './CallOffCommunicationTab';
import CallOffASNTab from './CallOffASNTab';

interface CallOffDetailModalProps {
  open: boolean;
  callOffId: number | null;
  onClose: () => void;
  onUpdated: () => void;
  /** Called when customer wants to open wizard in edit mode */
  onEditInWizard?: (callOff: CallOff) => void;
}

interface InlineEditChange {
  lineId: number;
  partNumber: string;
  oldQty: string;
  newQty: string;
}

const STATUS_PILL: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-green-100 text-green-700',
  dispatched: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function CallOffDetailModal({ open, callOffId, onClose, onUpdated, onEditInWizard }: CallOffDetailModalProps) {
  const { user } = useAuth();
  const { formatDate, formatDateTime } = useFormatters();
  const navigate = useNavigate();
  const [callOff, setCallOff] = useState<CallOff | null>(null);
  const [timeline, setTimeline] = useState<CallOffTimelineEvent[]>([]);
  const [editHistory, setEditHistory] = useState<CallOffEditHistoryEntry[]>([]);
  const [messages, setMessages] = useState<CallOffMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('detail');

  // Inline editing state (per-field, for customer)
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Supplier batch inline edit mode
  const [inlineEditMode, setInlineEditMode] = useState(false);
  const [inlineEditChanges, setInlineEditChanges] = useState<Record<number, InlineEditChange>>({});
  const [savingBatchEdit, setSavingBatchEdit] = useState(false);

  // Supplier add parts state
  const [showAddParts, setShowAddParts] = useState(false);
  const [availableParts, setAvailableParts] = useState<AvailablePartForCallOff[]>([]);
  const [availableDeliveries, setAvailableDeliveries] = useState<CallOffDeliveryDestination[]>([]);
  const [selectedDelivery, setSelectedDelivery] = useState<string>('');
  const [addPartsLoading, setAddPartsLoading] = useState(false);
  const [partQuantities, setPartQuantities] = useState<Record<number, number>>({});
  const [addingPart, setAddingPart] = useState(false);

  // Per-part PO availability fetched at edit-mode entry. Used to red-flag
  // warehouse lines whose qty exceeds what's free to call off.
  const [availabilityByPart, setAvailabilityByPart] = useState<Record<string, number>>({});

  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';
  const isSupplier = user?.company_type === 'supplier' || user?.platform === 'SUPPLIER';
  const isLogistics = user?.company_type === 'logistics';

  const fetchDetail = useCallback(async () => {
    if (!callOffId) return;
    setLoading(true);
    try {
      const [detailRes, timelineRes, historyRes, messagesRes] = await Promise.all([
        callOffAPI.getCallOff(callOffId),
        callOffAPI.getTimeline(callOffId),
        callOffAPI.getEditHistory(callOffId),
        callOffAPI.getMessages(callOffId),
      ]);
      setCallOff(detailRes.data);
      setTimeline(timelineRes.data || []);
      setEditHistory(historyRes.data || []);
      setMessages(messagesRes.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load call-off details';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [callOffId]);

  useEffect(() => {
    if (open && callOffId) {
      fetchDetail();
      setActiveTab('detail');
      setEditingLineId(null);
      setInlineEditMode(false);
      setInlineEditChanges({});
      setShowAddParts(false);
    }
  }, [open, callOffId, fetchDetail]);

  // Close on Escape (parity with PO modal)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleAction = async (action: 'send' | 'confirm' | 'dispatch' | 'cancel', skipValidation = false, reason?: string) => {
    if (!callOffId) return;
    setActionLoading(true);
    try {
      const actionMap = {
        send: () => callOffAPI.sendCallOff(callOffId, skipValidation),
        confirm: () => callOffAPI.confirmCallOff(callOffId),
        dispatch: () => callOffAPI.dispatchCallOff(callOffId),
        cancel: () => callOffAPI.cancelCallOff(callOffId, reason),
      };
      const res = await actionMap[action]();
      setCallOff(res.data);
      const labels = { send: 'sent', confirm: 'confirmed', dispatch: 'dispatched', cancel: 'cancelled' };
      toast.success(`Call-off ${labels[action]} successfully`);
      onUpdated();
      fetchDetail();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string; message?: string } } };
      if (action === 'send' && axiosErr.response?.data?.code === 'insufficient_po_coverage') {
        const override = window.confirm(
          `${axiosErr.response.data.message || 'Insufficient PO coverage.'}\n\nDo you want to send anyway without PO validation?`
        );
        if (override) {
          setActionLoading(false);
          handleAction('send', true);
          return;
        }
      } else {
        const message = axiosErr.response?.data?.message || (err instanceof Error ? err.message : `Failed to ${action} call-off`);
        toast.error(message);
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Per-field inline edit (customer)
  const startLineEdit = (lineId: number, field: string, currentValue: string) => {
    if (inlineEditMode) return;
    setEditingLineId(lineId);
    setEditingField(field);
    setEditValue(currentValue);
  };

  const cancelLineEdit = () => {
    setEditingLineId(null);
    setEditingField(null);
    setEditValue('');
  };

  const saveLineEdit = async () => {
    if (!callOffId || editingLineId === null || !editingField) return;
    setSavingEdit(true);
    try {
      await callOffAPI.inlineEdit(callOffId, editingLineId, editingField, editValue);
      toast.success('Updated');
      cancelLineEdit();
      fetchDetail();
      onUpdated();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Supplier batch inline edit
  const enterInlineEditMode = async () => {
    if (!callOffId || !callOff) return;
    setInlineEditMode(true);
    setInlineEditChanges({});
    setActiveTab('items');
    setAddPartsLoading(true);
    try {
      const [partsRes, deliveriesRes, availRes] = await Promise.all([
        callOffAPI.getAvailableParts(callOffId),
        callOffAPI.getAvailableDeliveries(callOffId),
        // Per-call-off endpoint scopes availability to the call-off's
        // customer + supplier, so suppliers get correct PO numbers (the
        // generic stock-planning endpoint scopes by the request user's
        // own company and returns 0 for suppliers).
        callOffAPI.getCallOffAvailability(callOffId),
      ]);
      setAvailableParts(partsRes.data || []);
      setAvailableDeliveries(deliveriesRes.data || []);
      const map: Record<string, number> = {};
      for (const [pn, qty] of Object.entries(availRes.data || {})) {
        map[pn] = parseFloat(qty) || 0;
      }
      setAvailabilityByPart(map);
    } catch {
      // Non-critical — degrade silently. Backend still validates on save.
      setAvailabilityByPart({});
    } finally {
      setAddPartsLoading(false);
    }
  };

  const cancelInlineEditMode = () => {
    setInlineEditMode(false);
    setInlineEditChanges({});
    setShowAddParts(false);
    fetchDetail();
  };

  const onQuantityChange = (line: CallOffLine, newQty: string) => {
    const parsed = parseFloat(newQty);
    if (isNaN(parsed) || parsed < 0) return;
    if (String(parsed) === line.requested_qty) {
      setInlineEditChanges(prev => {
        const updated = { ...prev };
        delete updated[line.id];
        return updated;
      });
    } else {
      setInlineEditChanges(prev => ({
        ...prev,
        [line.id]: { lineId: line.id, partNumber: line.part_number, oldQty: line.requested_qty, newQty: String(parsed) },
      }));
    }
  };

  const removeLineItem = (line: CallOffLine) => {
    if (!window.confirm(`Remove ${line.part_number} from this call-off?`)) return;
    onQuantityChange(line, '0');
  };

  const saveBatchInlineEdit = async () => {
    if (!callOffId) return;
    const changes = Object.values(inlineEditChanges);
    if (changes.length === 0) { toast.info('No changes to save'); return; }
    if (!window.confirm(`Save ${changes.length} change(s) to ${changes.length} part(s)?`)) return;
    setSavingBatchEdit(true);
    try {
      const batchChanges: BatchInlineEditChange[] = changes.map(c => ({ line_id: c.lineId, new_qty: parseFloat(c.newQty) }));
      await callOffAPI.batchInlineEdit(callOffId, batchChanges);
      toast.success(`${changes.length} change(s) saved`);
      setInlineEditMode(false);
      setInlineEditChanges({});
      fetchDetail();
      onUpdated();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to save changes'));
    } finally {
      setSavingBatchEdit(false);
    }
  };

  const handleAddPart = async (part: AvailablePartForCallOff) => {
    if (!callOffId) return;
    const qty = partQuantities[part.id] || 0;
    if (qty <= 0) { toast.error('Please enter a quantity greater than 0'); return; }
    if (!selectedDelivery) { toast.error('Please select a delivery destination'); return; }
    const delivery = availableDeliveries[parseInt(selectedDelivery)];
    if (!delivery) return;
    setAddingPart(true);
    try {
      await callOffAPI.addLines(callOffId, [{
        part_id: part.id,
        part_number: part.part_number,
        description: part.description,
        requested_qty: qty,
        pallet_qty: parseFloat(part.pallet_qty) || 0,
        load_qty: parseFloat(part.load_qty) || 0,
        delivery_type: delivery.delivery_type,
        delivery_address: delivery.delivery_address,
        delivery_destination_id: delivery.delivery_destination_id || undefined,
      }]);
      toast.success(`${part.part_number} added`);
      setPartQuantities(prev => ({ ...prev, [part.id]: 0 }));
      fetchDetail();
      onUpdated();
      const partsRes = await callOffAPI.getAvailableParts(callOffId);
      setAvailableParts(partsRes.data || []);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to add part');
    } finally {
      setAddingPart(false);
    }
  };

  const handleSendMessage = async () => {
    if (!callOffId || !newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const res = await callOffAPI.sendMessage(callOffId, newMessage.trim());
      setMessages(prev => [...prev, res.data]);
      setNewMessage('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const markMessagesRead = useCallback(async () => {
    if (!callOffId) return;
    const unreadCount = messages.filter(m => !m.is_read && m.sender_role !== (isCustomer ? 'customer' : 'supplier')).length;
    if (unreadCount === 0) return;
    try {
      await callOffAPI.markMessagesRead(callOffId);
      setMessages(prev => prev.map(m => ({ ...m, is_read: true, read_at: new Date().toISOString() })));
    } catch {
      // Non-critical
    }
  }, [callOffId, messages, isCustomer]);

  useEffect(() => {
    if (activeTab === 'communication' && messages.length > 0) {
      markMessagesRead();
    }
  }, [activeTab, markMessagesRead, messages.length]);

  if (!open) return null;

  const canEdit = callOff && ['draft', 'pending', 'confirmed'].includes(callOff.detail_status);
  const supplierCanEditLines = isSupplier && ['pending', 'confirmed'].includes(callOff?.detail_status || '');
  const changeCount = Object.keys(inlineEditChanges).length;
  const unreadCount = messages.filter(m => !m.is_read && m.sender_role !== (isCustomer ? 'customer' : 'supplier')).length;

  // Aggregate per part_number: warehouse lines sharing a part share a PO
  // budget. effective = raw available + sum(originalQty for those lines).
  // consumed = sum(currentQty after edits). Over if consumed > effective.
  const overByPart: Record<string, { effective: number; consumed: number; over: boolean }> = {};
  if (callOff && inlineEditMode) {
    const groups: Record<string, CallOffLine[]> = {};
    for (const line of callOff.lines) {
      if (line.delivery_type !== 'warehouse') continue;
      (groups[line.part_number] = groups[line.part_number] || []).push(line);
    }
    for (const [pn, lines] of Object.entries(groups)) {
      const raw = availabilityByPart[pn] || 0;
      const own = lines.reduce((s, l) => s + (parseFloat(l.requested_qty) || 0), 0);
      const consumed = lines.reduce((s, l) => {
        const ch = inlineEditChanges[l.id];
        const q = ch ? parseFloat(ch.newQty) : parseFloat(l.requested_qty);
        return s + (q || 0);
      }, 0);
      const effective = raw + own;
      overByPart[pn] = { effective, consumed, over: consumed > effective };
    }
  }
  const overLineCount = callOff ? callOff.lines.filter(
    l => l.delivery_type === 'warehouse' && overByPart[l.part_number]?.over
  ).length : 0;
  const hasOverLimit = overLineCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-h-[95vh] flex flex-col" style={{ maxWidth: '1400px', minWidth: '1200px' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-primary">
              Call-Off: {callOff?.reference || '...'}
            </h2>
            {callOff && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_PILL[callOff.detail_status] || STATUS_PILL['draft']}`}>
                {callOff.detail_status.charAt(0).toUpperCase() + callOff.detail_status.slice(1)}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading || !callOff ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b px-4 pt-2">
                <TabsList className="bg-transparent border-none">
                  <TabsTrigger
                    value="detail"
                    className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
                  >
                    Detail
                  </TabsTrigger>
                  <TabsTrigger
                    value="items"
                    className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
                  >
                    <span className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      Items ({callOff.lines?.length || 0})
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="asn"
                    className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
                  >
                    <span className="flex items-center gap-1">
                      <Ship className="h-4 w-4" />
                      ASN/Shipments
                    </span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="communication"
                    className="data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-primary"
                  >
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      Communication ({messages.length})
                      {unreadCount > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold">
                          {unreadCount}
                        </span>
                      )}
                    </span>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="detail" className="m-0 p-4">
                <CallOffDetailTab
                  callOff={callOff}
                  timeline={timeline}
                  editHistory={editHistory}
                  formatDate={formatDate}
                  formatDateTime={formatDateTime}
                />
              </TabsContent>

              <TabsContent value="items" className="m-0 p-4">
                <CallOffLinesTable
                  callOff={callOff}
                  inlineEditMode={inlineEditMode}
                  inlineEditChanges={inlineEditChanges}
                  editingLineId={editingLineId}
                  editingField={editingField}
                  editValue={editValue}
                  savingEdit={savingEdit}
                  supplierCanEditLines={supplierCanEditLines}
                  onQuantityChange={onQuantityChange}
                  onStartLineEdit={startLineEdit}
                  onSaveLineEdit={saveLineEdit}
                  onCancelLineEdit={cancelLineEdit}
                  setEditValue={setEditValue}
                  removeLineItem={removeLineItem}
                  showAddParts={showAddParts}
                  availableParts={availableParts}
                  availableDeliveries={availableDeliveries}
                  selectedDelivery={selectedDelivery}
                  setSelectedDelivery={setSelectedDelivery}
                  addPartsLoading={addPartsLoading}
                  partQuantities={partQuantities}
                  setPartQuantities={setPartQuantities}
                  addingPart={addingPart}
                  handleAddPart={handleAddPart}
                  isSupplier={isSupplier}
                  isLogistics={isLogistics}
                  overByPart={overByPart}
                />
              </TabsContent>

              <TabsContent value="asn" className="m-0 p-4">
                <CallOffASNTab callOffId={callOff.id} />
              </TabsContent>

              <TabsContent value="communication" className="m-0 p-4">
                <CallOffCommunicationTab
                  messages={messages}
                  isCustomer={isCustomer}
                  detailStatus={callOff.detail_status}
                  newMessage={newMessage}
                  setNewMessage={setNewMessage}
                  onSend={handleSendMessage}
                  sending={sendingMessage}
                />
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Footer — workflow actions left, Close right */}
        <div className="border-t p-4 flex items-center justify-between gap-3 bg-muted/10">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Supplier batch edit controls take priority when in edit mode */}
            {inlineEditMode ? (
              <>
                <Button
                  size="sm"
                  className="gap-1.5 bg-green-600 hover:bg-green-700"
                  onClick={saveBatchInlineEdit}
                  disabled={savingBatchEdit || changeCount === 0 || hasOverLimit}
                  title={hasOverLimit ? 'Reduce over-limit quantities first' : undefined}
                >
                  {savingBatchEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save Changes
                </Button>
                <Button size="sm" variant="outline" onClick={cancelInlineEditMode} disabled={savingBatchEdit}>
                  Cancel Edit
                </Button>
                {changeCount > 0 && (
                  <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                    {changeCount} change{changeCount !== 1 ? 's' : ''}
                  </span>
                )}
                {hasOverLimit && (
                  <span className="px-2 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-medium">
                    {overLineCount} item{overLineCount !== 1 ? 's' : ''} exceed available qty
                  </span>
                )}
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setShowAddParts(!showAddParts)}>
                  <Plus className="h-3.5 w-3.5" />
                  {showAddParts ? 'Hide Add Parts' : 'Add Parts'}
                </Button>
              </>
            ) : callOff && (
              <>
                {isCustomer && callOff.detail_status === 'draft' && (
                  <Button size="sm" className="gap-1.5" onClick={() => handleAction('send')} disabled={actionLoading}>
                    <Send className="h-3.5 w-3.5" /> Send to Supplier
                  </Button>
                )}
                {isCustomer && ['draft', 'pending', 'confirmed'].includes(callOff.detail_status) && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    onClick={() => {
                      const reason = window.prompt(
                        'Reason for cancelling this call-off (recorded on the call-off and visible to the supplier if already sent):'
                      );
                      if (reason === null) return;
                      const trimmed = reason.trim();
                      if (!trimmed) {
                        toast.error('A cancellation reason is required.');
                        return;
                      }
                      handleAction('cancel', false, trimmed);
                    }}
                    disabled={actionLoading}
                  >
                    <XCircle className="h-3.5 w-3.5" /> Cancel
                  </Button>
                )}
                {isCustomer && canEdit && onEditInWizard && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { onClose(); onEditInWizard(callOff); }}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                )}
                {isSupplier && callOff.detail_status === 'pending' && (
                  <Button size="sm" className="gap-1.5" onClick={() => handleAction('confirm')} disabled={actionLoading}>
                    <CheckCircle className="h-3.5 w-3.5" /> Confirm
                  </Button>
                )}
                {isSupplier && callOff.detail_status === 'confirmed' && (
                  <>
                    <Button
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        onClose();
                        navigate(`/purchasing?tab=asn&action=create&call_off=${callOff.id}`);
                      }}
                      disabled={actionLoading}
                    >
                      <Ship className="h-3.5 w-3.5" /> Create ASN
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Submit the ASN to mark this call-off as dispatched.
                    </span>
                  </>
                )}
                {isCustomer && callOff.detail_status === 'confirmed' && (
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleAction('dispatch')} disabled={actionLoading}>
                    <Truck className="h-3.5 w-3.5" /> Mark Dispatched
                  </Button>
                )}
                {supplierCanEditLines && (
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={enterInlineEditMode}>
                    <Pencil className="h-3.5 w-3.5" /> Edit Call-Off
                  </Button>
                )}
                {actionLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              </>
            )}
          </div>
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-lg hover:bg-muted font-medium">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
